import * as Sentry from "@sentry/react";
import { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./query-client";
import {
  ConfigurationProvider,
  useConfiguration,
} from "./contexts/ConfigurationProvider";
import { useBookingStoreHydration } from "./stores/useBookingStoreHydration";
import { NotFound } from "./components/NotFound";
import { SiteNotFound } from "./pages/not-found/SiteNotFound";
import { Toaster } from "react-hot-toast";
import Expired from "./pages/pay/Expired";
import { PayFooter } from "./pages/pay/PayFooter";
import { ReservationSessionProvider } from "./contexts/ReservationSessionProvider";
import { WBEAuthProvider } from "./contexts/WBEAuthProvider";
import { RequireWBEAuth } from "./pages/auth/RequireWBEAuth";
import { ConfirmationRedirect } from "./pages/confirmation/ConfirmationRedirect";
import { CheckoutRedirect } from "./pages/checkout/CheckoutRedirect";
import { useSiteDocumentTitle } from "./hooks/useSiteDocumentTitle";
import { PrivatePageGuard } from "./components/PrivatePageGuard";
import { communicationPreferencesPage } from "./private-pages";

const CHUNK_ERROR_KEY = "chunkLoadReload";

// On a rolling deploy the old chunk hash disappears. Catch the network 404,
// reload once to pick up the new index.html → new main bundle → correct hashes.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: Error) => {
      if (
        err.message.includes("Failed to fetch") &&
        !sessionStorage.getItem(CHUNK_ERROR_KEY)
      ) {
        sessionStorage.setItem(CHUNK_ERROR_KEY, "1");
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    }),
  );
}

const LandingV2 = lazyWithReload(() => import("./pages/landing-v2/LandingV2"));
// Same chunk as the layout above — resolved twice, downloaded once.
const V2Landing = lazyWithReload(() =>
  import("./pages/landing-v2/LandingV2").then((m) => ({ default: m.V2Landing })),
);
const V2Plan = lazyWithReload(() =>
  import("./pages/landing-v2/LandingV2").then((m) => ({ default: m.V2Plan })),
);
const Booking = lazyWithReload(() => import("./pages/booking/Booking"));
const ActivityCalendarDemo = lazyWithReload(
  () => import("./pages/dev/ActivityCalendarDemo"),
);
const Checkout = lazyWithReload(() => import("./pages/checkout/Checkout"));
const Pay = lazyWithReload(() =>
  import("./pages/pay/Pay").then((module) => ({ default: module.Pay })),
);
const AuthPage = lazyWithReload(() =>
  import("./pages/auth/AuthPage").then((module) => ({ default: module.AuthPage })),
);
const PasswordResetPage = lazyWithReload(() =>
  import("./pages/auth/PasswordResetPage").then((module) => ({
    default: module.PasswordResetPage,
  })),
);
const Confirmation = lazyWithReload(
  () => import("./pages/confirmation/Confirmation"),
);
const Confirmation2 = lazyWithReload(() => import("./pages/pay/Confirmation"));
const TermsAndPolicy = lazyWithReload(() => import("./pages/terms-and-policy"));
const CommunicationPreferencesPage = lazyWithReload(
  () =>
    import("./pages/communication-preferences/CommunicationPreferencesPage"),
);

const FallbackDiv = () => <div className="min-h-screen bg-surface-page" />;

/**
 * The site's start page, a layout route (see the route table): it owns
 * the reservation query, the flow provider and the searches, so crossing
 * between the landing and the plan never remounts them.
 *
 * Waits for `configuration`: the landing reads site settings that are empty
 * until it arrives. Not on `isLoading` — the provider keeps the last config
 * through a reload (a language switch re-runs its effect), so the landing
 * stays mounted for it.
 */
const LandingRoot = () => {
  const { configuration } = useConfiguration();
  if (!configuration) return <FallbackDiv />;
  return <LandingV2 />;
};

/** Root layout — footer on every page */
const RootLayout = () => (
  <div className="flex min-h-screen flex-col bg-surface-page pb-[80px] lg:pb-0">
    <main className="flex-1">
      <Outlet />
    </main>
    <PayFooter />
    <ScrollRestoration />
  </div>
);

const AppLayout = () => {
  useSiteDocumentTitle();
  return (
    <WBEAuthProvider>
      <ReservationSessionProvider>
        <Outlet />
      </ReservationSessionProvider>
    </WBEAuthProvider>
  );
};

/** Content wrapper for pages that need centered, constrained layout.
 *  Mobile padding is deliberately tighter than the `element` token: on a phone
 *  the page gutter competes with the cards inside it, and cards carry their own
 *  padding already. Desktop keeps the full section gutter. */
const PageContent = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-container px-3 md:px-section">
    <Suspense fallback={<FallbackDiv />}>{children}</Suspense>
  </div>
);

function App() {
  useBookingStoreHydration();

  const router = createBrowserRouter([
    {
      path: communicationPreferencesPage.path,
      element: (
        <PrivatePageGuard>
          <Suspense fallback={<FallbackDiv />}>
            <CommunicationPreferencesPage />
          </Suspense>
        </PrivatePageGuard>
      ),
    },
    {
      element: <RootLayout />,
      children: [
        {
          path: "/404",
          element: <NotFound />,
        },
        {
          path: "/p/:paymentId",
          element: (
            <Suspense fallback={<FallbackDiv />}>
              <Pay />
            </Suspense>
          ),
        },
        {
          path: "/p/:paymentId/confirmation",
          element: (
            <PageContent>
              <Confirmation2 />
            </PageContent>
          ),
        },
        {
          path: "/p/:paymentId/terms",
          element: (
            <Suspense fallback={<FallbackDiv />}>
              <TermsAndPolicy termType="general-terms" header="pay" />
            </Suspense>
          ),
        },
        {
          path: "/p/:paymentId/policy",
          element: (
            <Suspense fallback={<FallbackDiv />}>
              <TermsAndPolicy termType="integrity-policy" header="pay" />
            </Suspense>
          ),
        },
        {
          path: "/p/expired",
          element: (
            <PageContent>
              <Expired />
            </PageContent>
          ),
        },
        {
          element: <AppLayout />,
          children: [
            // DEV-only design playground; never mounted in a production
            // build. Site-prefixed so ConfigurationProvider resolves the slug
            // and the page can show the site's real availability.
            ...(import.meta.env.DEV
              ? [
                  {
                    path: "/:identifier/dev/activity-calendar",
                    element: (
                      <Suspense fallback={<FallbackDiv />}>
                        <ActivityCalendarDemo />
                      </Suspense>
                    ),
                  },
                ]
              : []),
            {
              path: "/:identifier/login",
              element: (
                <PageContent>
                  <AuthPage mode="login" />
                </PageContent>
              ),
            },
            {
              path: "/:identifier/signup",
              element: (
                <PageContent>
                  <AuthPage mode="signup" />
                </PageContent>
              ),
            },
            {
              path: "/:identifier/password-reset",
              element: (
                <PageContent>
                  <PasswordResetPage />
                </PageContent>
              ),
            },
            {
              element: <RequireWBEAuth />,
              children: [
                {
                  // The start page — see `LandingRoot`. The children are the landing at the
                  // root and the plan beside it. A layout route so
                  // crossing between the two never remounts the flow. The plan is a place
                  // rather than a piece of state — see
                  // `.ai/specs/2026-09-28-wbe-booking-flow-v2-plan-route.md`.
                  path: "/:identifier",
                  element: (
                    <Suspense fallback={<FallbackDiv />}>
                      <LandingRoot />
                    </Suspense>
                  ),
                  children: [
                    { index: true, element: <V2Landing /> },
                    { path: "plan", element: <V2Plan /> },
                  ],
                },
                {
                  path: "/:identifier/booking",
                  element: (
                    <Suspense fallback={<FallbackDiv />}>
                      <Booking />
                    </Suspense>
                  ),
                },
                {
                  // Legacy keyless entry — redirect to the canonical keyed URL
                  // using the stored key (or home if there's no cart).
                  path: "/:identifier/checkout",
                  element: <CheckoutRedirect />,
                },
                {
                  // Canonical checkout route: the reservation comes from the
                  // URL, so a link mailed by an agent opens the right cart in a
                  // browser that has never seen it.
                  path: "/:identifier/checkout/:reservationKey",
                  element: (
                    <Suspense fallback={<FallbackDiv />}>
                      <Checkout />
                    </Suspense>
                  ),
                },
                {
                  // Legacy keyless entry — redirect to the canonical keyed URL
                  // using the stored key (or home if there's nothing to show).
                  path: "/:identifier/confirmation",
                  element: <ConfirmationRedirect />,
                },
                {
                  // Canonical confirmation route: reservation identity comes
                  // from the URL, not session state, so the page always shows
                  // the booking that was paid for and survives refresh /
                  // back-forward (and the Swish mobile cold-return).
                  path: "/:identifier/confirmation/:reservationKey",
                  element: (
                    <Suspense fallback={<FallbackDiv />}>
                      <Confirmation />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "/:identifier/terms",
              element: (
                <Suspense fallback={<FallbackDiv />}>
                  <TermsAndPolicy termType="general-terms" header="site" />
                </Suspense>
              ),
            },
            {
              path: "/:identifier/policy",
              element: (
                <Suspense fallback={<FallbackDiv />}>
                  <TermsAndPolicy termType="integrity-policy" header="site" />
                </Suspense>
              ),
            },
            {
              // Unmatched path under a slug that resolves. Ranked below every
              // static sibling above, so it only catches what nothing else
              // claims. Outside RequireWBEAuth: a 404 must not ask for a login.
              path: "/:identifier/*",
              element: (
                <Suspense fallback={<FallbackDiv />}>
                  <SiteNotFound />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigurationProvider>
        <Sentry.ErrorBoundary fallback={<NotFound />}>
          <RouterProvider router={router} useTransitions={false} />
        </Sentry.ErrorBoundary>
        <Toaster position="bottom-center" />
      </ConfigurationProvider>
    </QueryClientProvider>
  );
}

export default App;
